import React, { useState, useEffect, useCallback } from 'react';
import axios, { API_URL } from '../api/axiosConfig';
import '../pages/Profiles.css';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';

const EditarTramiteModal = ({ isOpen, tramiteId, onClose, onSuccess }) => {
  const { user } = useContext(AuthContext);
  const [transitos, setTransitos] = useState([]);
  const [esOtro, setEsOtro] = useState(false); // inscripción de prenda: sin factura
  const [tramiteTypes, setTramiteTypes] = useState([]);
  const [plates, setPlates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPlateDropdown, setShowPlateDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPlates, setLoadingPlates] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [searchingOwner, setSearchingOwner] = useState(false);
  const [ownerChecked, setOwnerChecked] = useState(false);
  const [searchingAcreedor, setSearchingAcreedor] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [acreedorChecked, setAcreedorChecked] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [existingFiles, setExistingFiles] = useState([]);
  const [pendingPlateChange, setPendingPlateChange] = useState(null);
  const [initialTransitoId, setInitialTransitoId] = useState(null);

  const [formData, setFormData] = useState({
    transito_id: '',
    tipo_tramite_id: '',
    vehicle_type: '',
    propietario: '',
    tipo_documento_id: 'CC',
    documento: '',
    correo: '',
    telefono: '',
    direccion: '',
    placa_referencia: '',
    observaciones: '',
    chasis: '',
    motor: '',
    vin: '',
    vehicleDetails: null,
    acreedor_tipo_documento: 'NIT',
    acreedor_documento: '',
    acreedor_nombre: '',
    acreedor_estado_persona: null,
    nuevo_color: ''
  });

  const [files, setFiles] = useState({
    cedula: null,
    factura: null,
    declaracion_importacion: null,
    contrato_prenda: null
  });

  const [transitoConfig, setTransitoConfig] = useState(null);

  const selectedTramiteType = tramiteTypes.find(tt => tt.id === parseInt(formData.tipo_tramite_id));
  const isPrenda = selectedTramiteType ? selectedTramiteType.name.toUpperCase().includes('PRENDA') : false;
  const isCambioColor = selectedTramiteType ? selectedTramiteType.name.toUpperCase().includes('COLOR') : false;

  const fetchInitialData = useCallback(async () => {
    try {
      const [transRes, typesRes] = await Promise.all([
        axios.get('/api/transitos'),
        axios.get('/api/tramite-types')
      ]);
      setTransitos(transRes.data);
      setTramiteTypes(typesRes.data);
    } catch (err) {
      console.error('Error al cargar datos iniciales:', err);
    }
  }, []);

  const fetchTramiteDetails = useCallback(async () => {
    try {
      setFetchingDetails(true);
      const res = await axios.get(`/api/tramites/${tramiteId}`);
      const data = res.data;
      
      setFormData({
        transito_id: data.transito_id || '',
        tipo_tramite_id: data.tipo_tramite_id || '',
        vehicle_type: data.datos_adicionales?.vehicle_type || '',
        propietario: data.datos_adicionales?.propietario || '',
        tipo_documento_id: data.datos_adicionales?.tipo_documento || 'CC',
        documento: data.datos_adicionales?.documento || '',
        correo: data.datos_adicionales?.correo || '',
        telefono: data.datos_adicionales?.telefono || '',
        direccion: data.datos_adicionales?.direccion || '',
        placa_referencia: data.datos_adicionales?.placa_referencia || '',
        observaciones: data.datos_adicionales?.observaciones || '',
        chasis: data.datos_adicionales?.chasis || '',
        motor: data.datos_adicionales?.motor || '',
        vin: data.datos_adicionales?.vin || '',
        vehicleDetails: data.datos_adicionales?.vehicleDetails || null,
        acreedor_tipo_documento: data.datos_adicionales?.acreedor_tipo_documento || 'NIT',
        acreedor_documento: data.datos_adicionales?.acreedor_documento || '',
        acreedor_nombre: data.datos_adicionales?.acreedor_nombre || '',
        representante_legal_nombre: data.datos_adicionales?.representante_legal_nombre || '',
        representante_legal_tipo_documento: data.datos_adicionales?.representante_legal_tipo_documento || 'CC',
        representante_legal_documento: data.datos_adicionales?.representante_legal_documento || '',
        acreedor_estado_persona: data.datos_adicionales?.acreedor_estado_persona || null,
        nuevo_color: data.datos_adicionales?.nuevo_color || '',
        estado: data.estado || ''
      });
      setInitialTransitoId(data.transito_id || null);
      setPendingPlateChange(data.pending_plate_change || null);
      setSearchTerm(data.datos_adicionales?.placa_referencia || '');
      setExistingFiles(data.archivos || []);
      setEsOtro(data.datos_adicionales?.tipo_flujo === 'otros_tramites');
      setOwnerChecked(true);
      setAcreedorChecked(true);
    } catch (err) {
      console.error('Error al cargar detalles del trámite:', err);
      alert('❌ No se pudieron cargar los detalles del trámite');
    } finally {
      setFetchingDetails(false);
    }
  }, [tramiteId]);

  useEffect(() => {
    if (isOpen && tramiteId) {
      fetchInitialData();
      fetchTramiteDetails();
    } else {
      resetForm();
    }
  }, [isOpen, tramiteId, fetchInitialData, fetchTramiteDetails]);

  useEffect(() => {
    if (formData.transito_id) {
      const selectedTransito = transitos.find(t => t.id === parseInt(formData.transito_id));
      setTransitoConfig(selectedTransito);
      
      if (selectedTransito?.plate_assignment_mode === 'automatico') {
        const availablePlatesForType = plates.filter(p => p.vehicle_type === formData.vehicle_type && p.status === 'disponible');
        const nextPlate = availablePlatesForType.length > 0 ? availablePlatesForType[0].plate_number : 'PENDIENTE';
        
        if (formData.vehicle_type) {
          setSearchTerm(`AUTO: ${nextPlate}`);
          setFormData(prev => ({ ...prev, placa_referencia: 'AUTO' }));
        } else {
          setSearchTerm('SELECCIONE TIPO DE VEHÍCULO');
          setFormData(prev => ({ ...prev, placa_referencia: '' }));
        }
      }
    } else {
      setTransitoConfig(null);
    }
  }, [formData.transito_id, formData.vehicle_type, transitos, plates]);

  useEffect(() => {
    // Ignorar respuestas tardías de un tránsito anterior para no mezclar placas de otro tránsito
    let cancelled = false;
    const transitoId = formData.transito_id;
    const fetchPlates = async () => {
      if (transitoId) {
        try {
          setLoadingPlates(true);
          const url = formData.vehicle_type 
            ? `/api/transitos/${transitoId}/plates?vehicle_type=${formData.vehicle_type}`
            : `/api/transitos/${transitoId}/plates`;
          const res = await axios.get(url);
          if (cancelled) return;
          setPlates(res.data.filter(p => String(p.transito_id) === String(transitoId) && (p.status === 'disponible' || p.id === formData.vehicleDetails?.pre_assigned_plate_id || p.plate_number === formData.placa_referencia)));
        } catch (err) {
          if (cancelled) return;
          console.error('Error al cargar placas:', err);
          setPlates([]);
        } finally {
          if (!cancelled) setLoadingPlates(false);
        }
      } else {
        setPlates([]);
        setLoadingPlates(false);
      }
    };
    fetchPlates();
    return () => { cancelled = true; };
  }, [formData.transito_id, formData.vehicle_type, formData.vehicleDetails?.pre_assigned_plate_id, formData.placa_referencia]);

  const resetForm = () => {
    setFormData({
      transito_id: '',
      tipo_tramite_id: '',
      vehicle_type: '',
      propietario: '',
      tipo_documento_id: 'CC',
      documento: '',
      correo: '',
      telefono: '',
      direccion: '',
      placa_referencia: '',
      observaciones: '',
      chasis: '',
      motor: '',
      vin: '',
      vehicleDetails: null,
      acreedor_tipo_documento: 'NIT',
      acreedor_documento: '',
      acreedor_nombre: '',
      acreedor_estado_persona: null,
      nuevo_color: '',
      estado: ''
    });
    setFiles({ cedula: null, factura: null, declaracion_importacion: null, contrato_prenda: null });
    setExistingFiles([]);
    setSearchTerm('');
    setOwnerChecked(false);
    setAcreedorChecked(false);
    setMessage({ text: '', type: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const upperValue = (name !== 'transito_id' && name !== 'tipo_tramite_id' && name !== 'tipo_documento_id' && name !== 'acreedor_tipo_documento' && name !== 'correo' && name !== 'vehicle_type') 
      ? value.toUpperCase() 
      : value;

    setFormData({ ...formData, [name]: upperValue });
    
    if (name === 'transito_id') {
      setFormData(prev => ({ ...prev, transito_id: value, placa_referencia: '', tipo_tramite_id: '' }));
      setSearchTerm('');
    }
    if (name === 'documento' || name === 'tipo_documento_id') {
      setOwnerChecked(false);
      setMessage({ text: '', type: '' });
    }
    if (name === 'acreedor_documento' || name === 'acreedor_tipo_documento') {
      setAcreedorChecked(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // VALIDAR EXTENSIÓN DEL ARCHIVO
    const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
    const fileExt = file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      alert(`❌ TIPO DE ARCHIVO NO PERMITIDO\n\nEl archivo "${file.name}" es un archivo de formato .${fileExt}.\n\nPor favor, sube ÚNICAMENTE archivos PDF o Imágenes.\n\n⚠️ No se aceptan archivos de Word (.doc, .docx) ni Excel.`);
      e.target.value = '';
      return;
    }

    
    if (file.size > 6 * 1024 * 1024) {
      alert(`❌ El archivo ${file.name} supera los 6MB`);
      e.target.value = '';
      return;
    }
    
    setFiles({ ...files, [e.target.name]: file });
  };

  const searchOwner = async () => {
    if (!formData.documento) {
      alert('⚠️ Ingrese un número de documento para buscar');
      return;
    }

    try {
      setSearchingOwner(true);
      setMessage({ text: '', type: '' });
      const res = await axios.get(`/api/tramites/owner/${formData.documento}?tipo_documento_id=${formData.tipo_documento_id}`);
      
      if (res.data) {
        const tieneMultas = Boolean(res.data.tiene_multas);
        const estado = res.data.estado_persona;

        if (tieneMultas) {
          const confirmContinue = window.confirm(
            `🚨 ALERTA DE MULTAS / SIMIT:\n\n` +
            `El ciudadano ${res.data.propietario || ''} (${formData.tipo_documento_id}: ${formData.documento}) presenta multas o comparendos pendientes en SIMIT.\n\n` +
            `¿Desea continuar con la edición/actualización del trámite?`
          );

          if (!confirmContinue) {
            setMessage({
              text: '❌ Actualización cancelada debido a multas o comparendos pendientes del ciudadano.',
              type: 'error'
            });
            setSearchingOwner(false);
            return;
          }
        }

        setFormData(prev => ({
          ...prev,
          propietario: res.data.propietario || '',
          tipo_documento_id: res.data.tipo_documento_id || 'CC',
          correo: res.data.correo || '',
          telefono: res.data.telefono || '',
          direccion: res.data.direccion || '',
          estado_persona: res.data.estado_persona || null,
          tiene_multas: tieneMultas
        }));
        
        let warningText = '';
        if (!estado || !estado.toUpperCase().includes('ACTIV')) {
          warningText += `⚠️ ATENCIÓN: El ciudadano NO se encuentra ACTIVO en el RUNT (Estado actual: ${estado || 'NO INSCRITO'}). `;
        }
        if (tieneMultas) {
          warningText += `🚨 ALERTA SIMIT: El ciudadano presenta multas/comparendos pendientes.`;
        }

        if (warningText) {
          alert(warningText);
          setMessage({ text: warningText, type: 'info' });
        } else {
          setMessage({ text: '✅ Información del cliente cargada correctamente. Sin multas reportadas.', type: 'success' });
        }
      }
      setOwnerChecked(true);
    } catch (err) {
      console.error('Error al buscar cliente:', err);
      setOwnerChecked(true);
      if (err.response?.status === 404) {
        alert('ℹ️ Cliente no encontrado. Ingrese los datos manualmente.');
      } else if (err.response?.status === 403) {
        alert('❌ Acceso denegado a la API externa (posible límite de cupo excedido).');
      } else if (err.response?.status === 401) {
        alert('❌ No autorizado para consultar la API externa (token inválido).');
      } else {
        alert('❌ Error al buscar información del cliente');
      }
    } finally {
      setSearchingOwner(false);
    }
  };

  const searchAcreedor = async () => {
    if (!formData.acreedor_documento) {
      alert('⚠️ Ingrese el número de documento del acreedor prendario');
      return;
    }

    try {
      setSearchingAcreedor(true);
      setMessage({ text: '', type: '' });
      const res = await axios.get(`/api/tramites/owner/${formData.acreedor_documento}?tipo_documento_id=${formData.acreedor_tipo_documento}`);
      
      if (res.data) {
        setFormData(prev => ({
          ...prev,
          acreedor_nombre: res.data.propietario || '',
          acreedor_tipo_documento: res.data.tipo_documento_id || prev.acreedor_tipo_documento,
          acreedor_estado_persona: res.data.estado_persona || null
        }));
        
        const estado = res.data.estado_persona;
        if (!estado || !estado.toUpperCase().includes('ACTIV')) {
          alert('⚠️ ATENCIÓN: El acreedor prendario NO se encuentra ACTIVO en el RUNT. Estado actual: ' + (estado || 'NO INSCRITO'));
        } else {
          setMessage({ text: '✅ Información del acreedor prendario cargada correctamente', type: 'success' });
        }
      }
      setAcreedorChecked(true);
    } catch (err) {
      console.error('Error al buscar acreedor:', err);
      setAcreedorChecked(true);
      if (err.response?.status === 404) {
        alert('ℹ️ Acreedor no encontrado en sistema. Ingrese los datos manualmente.');
      } else {
        alert('❌ Error al buscar información del acreedor prendario');
      }
    } finally {
      setSearchingAcreedor(false);
    }
  };

  const handlePlateSelect = (plateNumber) => {
    setFormData({ ...formData, placa_referencia: plateNumber });
    setSearchTerm(plateNumber);
    setShowPlateDropdown(false);
  };

  const filteredPlates = plates.filter(p => 
    p.plate_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // La placa se puede editar en cualquier estado del trámite, salvo que ya haya sido
  // ejecutado (tramite_ejecutado) o esté en las etapas posteriores a la ejecución
  // (entrega de placa física) o esté rechazado/eliminado. Debe coincidir con la
  // validación del backend en updateTramite (estadosBloqueadosCambioPlaca).
  const ESTADOS_BLOQUEADOS_CAMBIO_PLACA = ['tramite_ejecutado', 'placa_en_camino', 'placa_entregada', 'placa_confirmada', 'incidencia_placa', 'rechazado', 'eliminado'];
  const puedeEditarPlaca = !formData.estado || !ESTADOS_BLOQUEADOS_CAMBIO_PLACA.includes(formData.estado);

  const filteredTramiteTypes = tramiteTypes.filter(tt => 
    formData.transito_id && tt.transito_id === parseInt(formData.transito_id) && tt.is_active !== false
  );

  const getExistingFile = (type) => {
    return existingFiles.find(f => f.tipo_documento === type);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.vehicle_type) {
      alert('⚠️ Debe seleccionar un tipo de vehículo');
      return;
    }

    if (isCambioColor && !formData.nuevo_color?.trim()) {
      alert('⚠️ Debe ingresar el nombre del nuevo color para trámites de cambio de color');
      return;
    }

    if (isPrenda) {
      if (!formData.acreedor_documento || !formData.acreedor_nombre) {
        alert('⚠️ Para trámites con prenda debe diligenciar el documento y nombre del acreedor prendario');
        return;
      }
      // Para usuarios no superadmin, permitir cambio a prenda sin contrato (se creará novedad automática)
      // Para superadmin, exigir el contrato
      if (user?.role === 'superadmin' && !getExistingFile('Contrato_Prenda') && !files.contrato_prenda) {
        alert('⚠️ Debe adjuntar el Contrato de Prenda');
        return;
      }
    }

    // Validar que si cambia de tránsito, debe seleccionar una nueva placa
    if (initialTransitoId && formData.transito_id && parseInt(formData.transito_id) !== parseInt(initialTransitoId)) {
      if (!formData.placa_referencia || formData.placa_referencia.trim() === '') {
        alert('⚠️ Debe seleccionar una placa en el nuevo Organismo de Tránsito para completar el traslado');
        return;
      }
      // Si la placa es la misma, no permitir el traslado
      if (formData.placa_referencia === formData.vehicleDetails?.placa_referencia) {
        alert('⚠️ No puede usar la misma placa. Debe seleccionar una placa diferente en el nuevo Organismo de Tránsito');
        return;
      }
    }
    
    const totalSize = Object.values(files).reduce((acc, file) => acc + (file?.size || 0), 0);
    if (totalSize > 15 * 1024 * 1024) {
      alert('❌ La suma total de los archivos cargados no puede superar los 15MB');
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    const data = new FormData();
    data.append('transito_id', formData.transito_id);
    data.append('tipo_tramite_id', formData.tipo_tramite_id);
    data.append('vehicle_type', formData.vehicle_type);
    data.append('placa_referencia', formData.placa_referencia);
    data.append('datos_adicionales', JSON.stringify({
      propietario: formData.propietario,
      tipo_documento: formData.tipo_documento_id,
      representante_legal_nombre: formData.tipo_documento_id === 'NIT' ? formData.representante_legal_nombre : undefined,
      representante_legal_tipo_documento: formData.tipo_documento_id === 'NIT' ? (formData.representante_legal_tipo_documento || 'CC') : undefined,
      representante_legal_documento: formData.tipo_documento_id === 'NIT' ? formData.representante_legal_documento : undefined,
      documento: formData.documento,
      correo: formData.correo,
      telefono: formData.telefono,
      direccion: formData.direccion,
      vehicle_type: formData.vehicle_type,
      placa_referencia: formData.placa_referencia,
      observaciones: formData.observaciones,
      chasis: formData.chasis,
      motor: formData.motor,
      vin: formData.vin,
      vehicleDetails: formData.vehicleDetails,
      is_prenda: isPrenda,
      acreedor_tipo_documento: isPrenda ? formData.acreedor_tipo_documento : undefined,
      acreedor_documento: isPrenda ? formData.acreedor_documento : undefined,
      acreedor_nombre: isPrenda ? formData.acreedor_nombre : undefined,
      acreedor_estado_persona: isPrenda ? formData.acreedor_estado_persona : undefined,
      nuevo_color: isCambioColor ? formData.nuevo_color : undefined
    }));

    if (files.cedula) data.append('Cedula', files.cedula);
    if (files.factura) data.append('Factura', files.factura);
    if (files.declaracion_importacion) data.append('Declaracion_Importacion', files.declaracion_importacion);
    if (files.contrato_prenda) data.append('Contrato_Prenda', files.contrato_prenda);

    // Descarga el PDF del CUPL pagado para que quien hace el cambio lo conserve
    const descargarCuplPagado = async (info) => {
      if (!info?.cupl_url) return false;
      try {
        const resp = await fetch(`${API_URL}${info.cupl_url}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `CUPL_PAGADO_${info.cupl_number}${info.placa ? '_' + info.placa : ''}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        return true;
      } catch (e) {
        console.error('No se pudo descargar el CUPL pagado:', e);
        return false;
      }
    };

    try {
      try {
        await axios.put(`/api/tramites/${tramiteId}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (err) {
        const info = err.response?.data;
        if (err.response?.status !== 409 || !info?.requiere_confirmacion_cupl_pagado) throw err;

        const valor = info.cupl_valor ? ` por $ ${Number(info.cupl_valor).toLocaleString('es-CO')}` : '';
        const ok = window.confirm(
          `⚠️ CUPL YA PAGADO\n\n` +
          `El trámite tiene el CUPL ${info.cupl_number}${valor} PAGADO.\n\n` +
          `Al cambiar el tipo de trámite, ese CUPL se desvinculará del trámite y habrá que generar uno nuevo. ` +
          `El pago se conserva en Pagos CUPL.\n\n` +
          `Si continúa, se descargará el PDF del CUPL pagado para que lo conserve.\n\n¿Desea continuar?`
        );
        if (!ok) {
          setMessage({ text: 'Cambio cancelado: el CUPL pagado se mantiene en el trámite.', type: 'info' });
          return;
        }
        const descargado = await descargarCuplPagado(info);
        if (!descargado) {
          const seguir = window.confirm('No se pudo descargar el PDF del CUPL pagado. ¿Desea continuar con el cambio de todas formas?');
          if (!seguir) return;
        }
        data.set('confirmar_cambio_cupl_pagado', 'true');
        await axios.put(`/api/tramites/${tramiteId}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      alert('✅ Trámite modificado correctamente');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1000);
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.message || 'No se pudo actualizar el trámite'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '800px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h3>✏️ Editar Trámite #{tramiteId}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        
        {fetchingDetails ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>Cargando información del trámite...</p>
          </div>
        ) : (
          <>
            {message.text && (
              <div className={`
                ${message.type === 'success' ? 'success-banner' : ''}
                ${message.type === 'error' ? 'error-banner' : ''}
                ${message.type === 'info' ? 'info-banner' : ''}
              `}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-section">
                <div className="form-section-header">
                  <div className="form-section-icon">📋</div>
                  <h4 className="form-section-title">DATOS DEL TRÁMITE</h4>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>ORGANISMO DE TRÁNSITO <span style={{ color: 'red' }}>*</span></label>
                    <select name="transito_id" value={formData.transito_id} onChange={handleInputChange} required>
                      <option value="">seleccione...</option>
                      {transitos.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>TIPO DE TRÁMITE <span style={{ color: 'red' }}>*</span></label>
                    <select name="tipo_tramite_id" value={formData.tipo_tramite_id} onChange={handleInputChange} required disabled={!formData.transito_id}>
                      <option value="">
                        {!formData.transito_id 
                          ? 'seleccione primero un tránsito' 
                          : filteredTramiteTypes.length === 0 
                            ? 'no hay trámites asociados a este tránsito' 
                            : 'seleccione...'}
                      </option>
                      {filteredTramiteTypes.map(tt => <option key={tt.id} value={tt.id}>{tt.name}</option>)}
                    </select>
                  </div>
                </div>

                {initialTransitoId && formData.transito_id && parseInt(formData.transito_id) !== parseInt(initialTransitoId) && (
                  <div style={{ marginTop: '12px', padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', color: '#1e40af', fontSize: '13px', fontWeight: '600' }}>
                    ℹ️ <strong>Atención Solicitud de Traslado:</strong> Al seleccionar un Organismo de Tránsito distinto, se enviará una <strong>Solicitud de Eliminación de Placa</strong> al Tránsito actual. Una vez que este la apruebe, el trámite quedará oficialmente radicado en el nuevo Tránsito escogido.
                  </div>
                )}

                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>TIPO DE VEHÍCULO <span style={{ color: 'red' }}>*</span></label>
                  <div style={{ display: 'flex', gap: '25px', marginTop: '10px', background: 'var(--secondary-soft)', padding: '12px', borderRadius: '8px', border: '1px solid var(--card-border)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      <input 
                        type="radio" 
                        name="vehicle_type"
                        value="carro"
                        checked={formData.vehicle_type === 'carro'} 
                        onChange={handleInputChange}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      🚗 CARRO
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      <input 
                        type="radio" 
                        name="vehicle_type"
                        value="moto"
                        checked={formData.vehicle_type === 'moto'} 
                        onChange={handleInputChange}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      MOTO
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      <input 
                        type="radio" 
                        name="vehicle_type"
                        value="moto_carro"
                        checked={formData.vehicle_type === 'moto_carro'} 
                        onChange={handleInputChange}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      MOTO CARRO
                    </label>
                  </div>
                </div>

                {isCambioColor && (
                  <div className="form-group" style={{ marginTop: '15px' }}>
                    <label>NUEVO COLOR DEL VEHÍCULO <span style={{ color: 'red' }}>*</span></label>
                    <input 
                      type="text" 
                      name="nuevo_color" 
                      value={formData.nuevo_color} 
                      onChange={handleInputChange} 
                      required 
                      placeholder="Escriba el nombre del nuevo color (ej: ROJO, NEGRO MATE, AZUL Y BLANCO...)" 
                      style={{ textTransform: 'uppercase', fontWeight: 'bold', borderColor: '#2563eb' }}
                    />
                  </div>
                )}
              </div>

              <div className="form-section" style={!ownerChecked ? { borderLeft: '4px solid var(--primary-color)' } : {}}>
                <div className="form-section-header">
                  <div className="form-section-icon">🔍</div>
                  <h4 className="form-section-title">VERIFICACIÓN DE IDENTIDAD</h4>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', marginTop: '12px', gap: '12px' }}>
                  <div className="form-group">
                    <label>TIPO DOC. <span style={{ color: 'red' }}>*</span></label>
                    <select name="tipo_documento_id" value={formData.tipo_documento_id} onChange={handleInputChange} required>
                      <option value="CC">cédula de ciudadanía</option>
                      <option value="NIT">nit</option>
                      <option value="CE">cédula de extranjería</option>
                      <option value="PP">pasaporte</option>
                      <option value="PPT">permiso de permanencia temporal (PPT)</option>
                      <option value="TI">tarjeta de identidad</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>NRO DE DOCUMENTO <span style={{ color: 'red' }}>*</span></label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input 
                        type="text" 
                        name="documento" 
                        value={formData.documento} 
                        onChange={handleInputChange} 
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchOwner())}
                        required 
                        placeholder="número de documento" 
                        style={{ flex: 1 }}
                      />
                      <button 
                        type="button" 
                        onClick={searchOwner} 
                        className="btn-action btn-modules"
                        disabled={searchingOwner}
                        style={{ padding: '0 20px', height: '45px', fontWeight: '800' }}
                      >
                        {searchingOwner ? '⏳' : 'CONSULTAR'}
                      </button>
                    </div>
                  </div>
                </div>
                {formData.tipo_documento_id === 'NIT' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr', marginTop: '12px', gap: '12px' }}>
                    <div className="form-group">
                      <label>REPRESENTANTE LEGAL <span style={{ color: 'red' }}>*</span></label>
                      <input type="text" name="representante_legal_nombre" value={formData.representante_legal_nombre || ''} onChange={handleInputChange} required placeholder="nombre completo" />
                    </div>
                    <div className="form-group">
                      <label>TIPO DOC. R.L.</label>
                      <select name="representante_legal_tipo_documento" value={formData.representante_legal_tipo_documento || 'CC'} onChange={handleInputChange}>
                        <option value="CC">CC</option>
                        <option value="CE">CE</option>
                        <option value="PP">PASAPORTE</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>DOCUMENTO R.L. <span style={{ color: 'red' }}>*</span></label>
                      <input type="text" name="representante_legal_documento" value={formData.representante_legal_documento || ''} onChange={handleInputChange} required placeholder="número" />
                    </div>
                  </div>
                )}
              </div>

              {ownerChecked && (
                <>
                  <div className="form-section">
                    <div className="form-section-header">
                      <div className="form-section-icon">👤</div>
                      <h4 className="form-section-title">INFORMACIÓN DEL PROPIETARIO</h4>
                    </div>
                    
                    <div className="form-group">
                      <label>NOMBRE COMPLETO <span style={{ color: 'red' }}>*</span></label>
                      <input type="text" name="propietario" value={formData.propietario} onChange={handleInputChange} required placeholder="nombre del propietario" />
                    </div>

                    <div className="form-grid">
                      <div className="form-group">
                        <label>CORREO ELECTRÓNICO</label>
                        <input type="email" name="correo" value={formData.correo} onChange={handleInputChange} placeholder="ejemplo@correo.com" />
                      </div>
                      <div className="form-group">
                        <label>TELÉFONO</label>
                        <input type="text" name="telefono" value={formData.telefono} onChange={handleInputChange} placeholder="número de contacto" />
                      </div>
                    </div>

                    <div className="form-group" style={{ marginBottom: '0' }}>
                      <label>DIRECCIÓN</label>
                      <input type="text" name="direccion" value={formData.direccion} onChange={handleInputChange} placeholder="dirección de residencia" />
                    </div>
                  </div>

                  {isPrenda && (
                    <div className="form-section">
                      <div className="form-section-header">
                        <div className="form-section-icon">🏦</div>
                        <h4 className="form-section-title">INFORMACIÓN DEL ACREEDOR PRENDARIO</h4>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', marginTop: '12px', gap: '12px' }}>
                        <div className="form-group">
                          <label>TIPO DE IDENTIFICACIÓN ACREEDOR <span style={{ color: 'red' }}>*</span></label>
                          <select name="acreedor_tipo_documento" value={formData.acreedor_tipo_documento} onChange={handleInputChange} required={isPrenda}>
                            <optgroup label="PERSONA NATURAL">
                              <option value="CC">Cédula de Ciudadanía</option>
                              <option value="CE">Cédula de Extranjería</option>
                              <option value="PP">Pasaporte</option>
                              <option value="PPT">Permiso de Permanencia Temporal (PPT)</option>
                              <option value="TI">Tarjeta de Identidad</option>
                            </optgroup>
                            <optgroup label="PERSONA JURÍDICA">
                              <option value="NIT">NIT - Registro Mercantil</option>
                            </optgroup>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>ID PERSONA NATURAL / REGISTRO MERCANTIL ACREEDOR <span style={{ color: 'red' }}>*</span></label>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <input 
                              type="text" 
                              name="acreedor_documento" 
                              value={formData.acreedor_documento} 
                              onChange={handleInputChange} 
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchAcreedor())}
                              required={isPrenda}
                              placeholder="número de documento del acreedor" 
                              style={{ flex: 1 }}
                            />
                            <button 
                              type="button" 
                              onClick={searchAcreedor} 
                              className="btn-action btn-modules"
                              disabled={searchingAcreedor}
                              style={{ padding: '0 20px', height: '45px', fontWeight: '800' }}
                            >
                              {searchingAcreedor ? '⏳' : 'CONSULTAR'}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="form-group" style={{ marginTop: '12px', marginBottom: '0' }}>
                        <label>NOMBRE O RAZÓN SOCIAL DEL ACREEDOR <span style={{ color: 'red' }}>*</span></label>
                        <input 
                          type="text" 
                          name="acreedor_nombre" 
                          value={formData.acreedor_nombre} 
                          onChange={handleInputChange} 
                          required={isPrenda}
                          placeholder="Nombre o razón social del acreedor prendario" 
                        />
                      </div>
                    </div>
                  )}

                  <div className="form-section">
                    <div className="form-section-header">
                      <div className="form-section-icon">📂</div>
                      <div>
                        <h4 className="form-section-title">DOCUMENTACIÓN DE SOPORTE</h4>
                        <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '2px', fontWeight: 'bold' }}>
                          ⚠️ ÚNICAMENTE SE ACEPTAN ARCHIVOS <span style={{ color: '#e11d48' }}>PDF O IMÁGENES</span>. (No Word / No Excel)
                        </span>
                      </div>
                    </div>
                    
                    <div className="form-grid">
                      <div className="form-group">
                        <label>
                          {formData.tipo_documento_id === 'NIT'
                            ? 'CÁMARA DE COMERCIO / RUT (PDF/IMG)'
                            : 'ID PERSONA NATURAL / REGISTRO MERCANTIL PERSONA JURÍDICA (PDF/IMG)'}
                        </label>
                        {formData.tipo_documento_id === 'NIT' && (
                          <small style={{ color: '#2563eb', display: 'block', marginTop: '2px', fontWeight: 'bold' }}>
                            ⚠️ Para NIT solo se carga Cámara de Comercio / RUT como documento de identificación.
                          </small>
                        )}
                        {getExistingFile('Cedula') && (
                          <div style={{ fontSize: '11px', color: '#16a34a', marginBottom: '5px' }}>
                            ✓ Archivo actual: <em>{getExistingFile('Cedula').nombre_archivo}</em>
                          </div>
                        )}
                        <input type="file" name="cedula" onChange={handleFileChange} className="file-input" accept=".pdf,image/*" />
                        <small style={{ color: '#64748b' }}>Deje en blanco si no desea reemplazar el archivo</small>
                      </div>
                      {!esOtro && (
                      <div className="form-group">
                        <label>FACTURA (PDF/IMG)</label>
                        {getExistingFile('Factura') && (
                          <div style={{ fontSize: '11px', color: '#16a34a', marginBottom: '5px' }}>
                            ✓ Archivo actual: <em>{getExistingFile('Factura').nombre_archivo}</em>
                          </div>
                        )}
                        <input type="file" name="factura" onChange={handleFileChange} className="file-input" accept=".pdf,image/*" />
                        <small style={{ color: '#64748b' }}>Deje en blanco si no desea reemplazar el archivo</small>
                      </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>DECLARACIÓN DE IMPORTACIÓN / MANIFIESTO (PDF/IMG)</label>
                      {getExistingFile('Declaracion_Importacion') && (
                        <div style={{ fontSize: '11px', color: '#16a34a', marginBottom: '5px' }}>
                          ✓ Archivo actual: <em>{getExistingFile('Declaracion_Importacion').nombre_archivo}</em>
                        </div>
                      )}
                      <input type="file" name="declaracion_importacion" onChange={handleFileChange} className="file-input" accept=".pdf,image/*" />
                      <small style={{ color: '#64748b' }}>Deje en blanco si no desea reemplazar el archivo</small>
                    </div>

                    {isPrenda && (
                      <div className="form-group">
                        <label>CONTRATO DE PRENDA (PDF/IMG)</label>
                        {getExistingFile('Contrato_Prenda') && (
                          <div style={{ fontSize: '11px', color: '#16a34a', marginBottom: '5px' }}>
                            ✓ Archivo actual: <em>{getExistingFile('Contrato_Prenda').nombre_archivo}</em>
                          </div>
                        )}
                        <input type="file" name="contrato_prenda" onChange={handleFileChange} className="file-input" accept=".pdf,image/*" />
                        <small style={{ color: '#64748b' }}>Deje en blanco si no desea reemplazar el archivo</small>
                      </div>
                    )}

                    <div className="form-group" style={{ marginBottom: '0' }}>
                      <label>OBSERVACIONES</label>
                      <textarea name="observaciones" value={formData.observaciones} onChange={handleInputChange} rows="2" placeholder="detalles adicionales..." />
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="form-section-header">
                      <div className="form-section-icon">🚗</div>
                      <h4 className="form-section-title">DETALLES DEL VEHÍCULO</h4>
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: '0' }}>
                      <label>PLACA DE REFERENCIA {transitoConfig?.plate_assignment_mode === 'automatico' ? '(AUTOMÁTICA)' : '(MANUAL)'}</label>
                      <div className="searchable-select-container">
                        <input
                          type="text"
                          name="placa_referencia"
                          placeholder={
                            !formData.transito_id ? "seleccione primero un tránsito" :
                            transitoConfig?.plate_assignment_mode === 'automatico' ? "se asignará automáticamente..." :
                            "buscar placa disponible..."
                          }
                          value={searchTerm}
                          onChange={(e) => {
                            if (transitoConfig?.plate_assignment_mode === 'automatico') return;
                            // Lo escrito solo filtra la lista; la placa cambia únicamente al elegir una opción
                            // (handlePlateSelect). Si no se elige nada, se mantiene la placa que ya tenía.
                            setSearchTerm(e.target.value.toUpperCase());
                            setShowPlateDropdown(true);
                          }}
                          onBlur={() => {
                            setTimeout(() => {
                              setShowPlateDropdown(false);
                              setFormData(prev => {
                                setSearchTerm(prev.placa_referencia || '');
                                return prev;
                              });
                            }, 200);
                          }}
                          onFocus={() => {
                            if (transitoConfig?.plate_assignment_mode === 'manual' && formData.transito_id) {
                              setShowPlateDropdown(true);
                            }
                          }}
                          autoComplete="off"
                          disabled={!formData.transito_id || transitoConfig?.plate_assignment_mode === 'automatico' || !puedeEditarPlaca}
                          style={(transitoConfig?.plate_assignment_mode === 'automatico' || !puedeEditarPlaca) ? { background: 'var(--secondary-soft)', color: 'var(--text-secondary)', fontWeight: '700' } : {}}
                        />
                        {showPlateDropdown && transitoConfig?.plate_assignment_mode === 'manual' && formData.transito_id && puedeEditarPlaca && (
                          <div className="searchable-dropdown">
                            {loadingPlates ? (
                              <div className="dropdown-no-results" style={{ padding: '14px', textAlign: 'center', color: 'var(--primary-color)', fontWeight: '700', fontSize: '13px' }}>
                                <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', marginRight: '8px' }}>⏳</span>
                                Cargando placas disponibles...
                              </div>
                            ) : filteredPlates.length > 0 ? (
                              filteredPlates.map(p => (
                                <div key={p.id} className="dropdown-item" onClick={() => handlePlateSelect(p.plate_number)}>
                                  <span className="plate-badge-sm">{p.plate_number}</span>
                                  <small>({p.vehicle_type})</small>
                                </div>
                              ))
                            ) : (
                              <div className="dropdown-no-results">no hay placas disponibles</div>
                            )}
                          </div>
                        )}
                      </div>
                      {transitoConfig?.plate_assignment_mode === 'automatico' && (
                        <small style={{ color: 'var(--primary-color)', fontWeight: '600', marginTop: '4px', display: 'block' }}>
                          ℹ️ el sistema seleccionará la siguiente placa disponible para este tránsito.
                        </small>
                      )}
                      {pendingPlateChange ? (
                        <div style={{ marginTop: '8px', padding: '10px 12px', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a', color: '#b45309', fontSize: '12px', fontWeight: '700' }}>
                          ⏳ Solicitud de cambio pendiente de aprobación del Tránsito: Placa reservada <strong>{pendingPlateChange.requested_plate_number}</strong>.
                        </div>
                      ) : (
                        transitoConfig?.plate_assignment_mode === 'manual' && puedeEditarPlaca && formData.estado !== 'radicado' && (
                          <small style={{ color: '#0284c7', fontWeight: '600', marginTop: '4px', display: 'block' }}>
                            ℹ️ Si selecciona una nueva placa, se creará una solicitud de aprobación para el Tránsito y la nueva placa quedará reservada.
                          </small>
                        )
                      )}
                      {formData.estado && !puedeEditarPlaca && (
                        <small style={{ color: '#ea580c', fontWeight: '700', marginTop: '4px', display: 'block' }}>
                          ⚠️ No se puede cambiar la placa de un trámite ya ejecutado (Estado actual: {formData.estado.toUpperCase()}).
                        </small>
                      )}
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>NÚMERO DE CHASIS</label>
                        <input 
                          type="text" 
                          name="chasis" 
                          value={formData.chasis} 
                          onChange={handleInputChange} 
                          placeholder="Número de chasis" 
                          style={formData.chasis ? { backgroundColor: '#e8f5e9', borderColor: '#4caf50' } : {}} 
                        />
                      </div>
                      <div className="form-group">
                        <label>NÚMERO DE MOTOR</label>
                        <input 
                          type="text" 
                          name="motor" 
                          value={formData.motor} 
                          onChange={handleInputChange} 
                          placeholder="Número de motor" 
                          style={formData.motor ? { backgroundColor: '#e8f5e9', borderColor: '#4caf50' } : {}} 
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>NÚMERO DE VIN</label>
                      <input 
                        type="text" 
                        name="vin" 
                        value={formData.vin} 
                        onChange={handleInputChange} 
                        placeholder="Número de VIN" 
                        style={formData.vin ? { backgroundColor: '#e8f5e9', borderColor: '#4caf50' } : {}} 
                      />
                    </div>
                  </div>

                  <div className="modal-actions" style={{ padding: '0 20px 20px 20px', marginTop: '0' }}>
                    <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>cancelar</button>
                    <button type="submit" className="btn-primary" disabled={loading} style={{ padding: '10px 25px', fontSize: '13px', fontWeight: '800' }}>
                      {loading ? '🚀 GUARDANDO...' : '🚀 GUARDAR CAMBIOS'}
                    </button>
                  </div>
                </>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default EditarTramiteModal;
